// ============================================================
// HipWear — Geometry Utility Functions
// ============================================================

import * as THREE from 'three';

/**
 * Compute the centroid of a position array (xyz interleaved).
 */
export function computeCentroid(positions: Float32Array): THREE.Vector3 {
  const count = positions.length / 3;
  let sx = 0, sy = 0, sz = 0;
  for (let i = 0; i < positions.length; i += 3) {
    sx += positions[i];
    sy += positions[i + 1];
    sz += positions[i + 2];
  }
  return new THREE.Vector3(sx / count, sy / count, sz / count);
}

/**
 * Compute the bounding box of a position array.
 */
export function computeBounds(positions: Float32Array): THREE.Box3 {
  const box = new THREE.Box3();
  const v = new THREE.Vector3();
  for (let i = 0; i < positions.length; i += 3) {
    v.set(positions[i], positions[i + 1], positions[i + 2]);
    box.expandByPoint(v);
  }
  return box;
}

/**
 * Compute face normal from three vertices.
 */
export function faceNormal(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number
): [number, number, number] {
  const abx = bx - ax, aby = by - ay, abz = bz - az;
  const acx = cx - ax, acy = cy - ay, acz = cz - az;
  let nx = aby * acz - abz * acy;
  let ny = abz * acx - abx * acz;
  let nz = abx * acy - aby * acx;
  const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
  if (len > 1e-12) {
    nx /= len; ny /= len; nz /= len;
  }
  return [nx, ny, nz];
}

/**
 * Compute a tilted rim-plane normal by rotating the cup axis around a perpendicular
 * direction by `inclinationDeg` degrees, with the tilt pointing in the direction
 * specified by `azimuthDeg` (measured in the plane perpendicular to the cup axis).
 *
 * inclinationDeg = 0  → normal equals the cup axis (no tilt)
 * inclinationDeg = 30 → normal tilted 30° relative to the cup axis
 */
export function computeTiltedRimNormal(
  cupAxis: [number, number, number],
  inclinationDeg: number,
  azimuthDeg: number,
): THREE.Vector3 {
  const ax = new THREE.Vector3(...cupAxis).normalize();

  if (Math.abs(inclinationDeg) < 1e-6) return ax;

  // Build two orthonormal vectors in the plane perpendicular to the cup axis
  const tmp = Math.abs(ax.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
  const e1 = new THREE.Vector3().crossVectors(ax, tmp).normalize();
  const e2 = new THREE.Vector3().crossVectors(ax, e1).normalize();

  const azRad = azimuthDeg * (Math.PI / 180);
  const incRad = inclinationDeg * (Math.PI / 180);

  // Tilt direction in the perpendicular plane
  const tiltDir = new THREE.Vector3()
    .addScaledVector(e1, Math.cos(azRad))
    .addScaledVector(e2, Math.sin(azRad));

  // Rotate cup axis by inclinationDeg toward tiltDir
  const result = new THREE.Vector3()
    .addScaledVector(ax, Math.cos(incRad))
    .addScaledVector(tiltDir, Math.sin(incRad));

  return result.normalize();
}

/**
 * Compute the signed volume of a tetrahedron formed by a triangle and the origin.
 * Using the divergence theorem: V = (1/6) * v1 . (v2 x v3)
 */
export function signedTetraVolume(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number
): number {
  return (
    ax * (by * cz - bz * cy) +
    ay * (bz * cx - bx * cz) +
    az * (bx * cy - by * cx)
  ) / 6.0;
}

/**
 * Compute the area of a triangle.
 */
export function triangleArea(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number
): number {
  const abx = bx - ax, aby = by - ay, abz = bz - az;
  const acx = cx - ax, acy = cy - ay, acz = cz - az;
  const nx = aby * acz - abz * acy;
  const ny = abz * acx - abx * acz;
  const nz = abx * acy - aby * acx;
  return 0.5 * Math.sqrt(nx * nx + ny * ny + nz * nz);
}

/**
 * Euclidean distance between two 3D points.
 */
export function dist3(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number
): number {
  const dx = bx - ax, dy = by - ay, dz = bz - az;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * Dot product of two 3D vectors.
 */
export function dot3(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number
): number {
  return ax * bx + ay * by + az * bz;
}

/**
 * Normalize a 3D vector in-place, return length.
 */
export function normalize3(v: [number, number, number]): number {
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  if (len > 1e-12) {
    v[0] /= len; v[1] /= len; v[2] /= len;
  }
  return len;
}

/**
 * Cross product of two 3D vectors.
 */
export function cross3(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number
): [number, number, number] {
  return [
    ay * bz - az * by,
    az * bx - ax * bz,
    ax * by - ay * bx,
  ];
}

/**
 * Best-fit plane through an arbitrary number of 3-D points (≥3).
 * Uses Newell's method on all triangle fans from the centroid, which gives a
 * weighted-area normal that degrades gracefully to a least-squares normal.
 * Returns the plane normal (unit length, oriented towards the mesh centroid
 * direction passed in as `orientHint`) and the centroid of the point set.
 */
export function fitPlaneFromPoints(
  pts: THREE.Vector3[],
  orientHint?: THREE.Vector3,
): { normal: THREE.Vector3; center: THREE.Vector3 } {
  if (pts.length < 3) throw new Error('fitPlaneFromPoints requires at least 3 points');

  // Centroid
  const center = new THREE.Vector3();
  for (const p of pts) center.add(p);
  center.divideScalar(pts.length);

  // Newell's method: accumulate cross-products of consecutive edges around
  // the centroid to get a robust area-weighted normal estimate.
  let nx = 0, ny = 0, nz = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i].clone().sub(center);
    const b = pts[(i + 1) % pts.length].clone().sub(center);
    nx += a.y * b.z - a.z * b.y;
    ny += a.z * b.x - a.x * b.z;
    nz += a.x * b.y - a.y * b.x;
  }

  const normal = new THREE.Vector3(nx, ny, nz).normalize();

  // Flip so the normal points towards `orientHint` if provided
  if (orientHint && normal.dot(orientHint) < 0) {
    normal.negate();
  }

  return { normal, center };
}

/**
 * Decompose a plane normal back into (inclinationDeg, azimuthDeg) relative to
 * a cup axis, inverting the logic of `computeTiltedRimNormal`.
 *
 * inclinationDeg = angle between the plane normal and the cup axis (0–180)
 * azimuthDeg     = rotation of the tilt direction around the cup axis
 *
 * The returned values can be fed directly into `rimInclinationAngle` and
 * `rimInclinationAzimuth` in AnalysisParams.
 */
export function decomposeNormalToInclination(
  normal: THREE.Vector3,
  cupAxis: [number, number, number],
): { inclinationDeg: number; azimuthDeg: number } {
  const ax = new THREE.Vector3(...cupAxis).normalize();
  const n = normal.clone().normalize();

  // Inclination = angle between normal and cup axis
  const cosInc = Math.max(-1, Math.min(1, n.dot(ax)));
  const inclinationDeg = Math.acos(cosInc) * (180 / Math.PI);

  if (inclinationDeg < 1e-4) {
    // Normal is parallel to cup axis — azimuth is undefined, use 0
    return { inclinationDeg: 0, azimuthDeg: 0 };
  }

  // Build the same e1/e2 basis as computeTiltedRimNormal
  const tmp = Math.abs(ax.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
  const e1 = new THREE.Vector3().crossVectors(ax, tmp).normalize();
  const e2 = new THREE.Vector3().crossVectors(ax, e1).normalize();

  // Tilt direction = projection of n onto the plane perpendicular to ax, normalized
  const tilt = n.clone().addScaledVector(ax, -cosInc).normalize();

  const azimuthDeg = Math.atan2(tilt.dot(e2), tilt.dot(e1)) * (180 / Math.PI);

  return { inclinationDeg, azimuthDeg };
}

/**
 * Project a point onto a sphere surface.
 */
export function projectOntoSphere(
  px: number, py: number, pz: number,
  cx: number, cy: number, cz: number,
  radius: number
): [number, number, number] {
  const dx = px - cx, dy = py - cy, dz = pz - cz;
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (d < 1e-12) return [cx + radius, cy, cz];
  const scale = radius / d;
  return [cx + dx * scale, cy + dy * scale, cz + dz * scale];
}

/**
 * Compute spherical coordinates (latitude, longitude) relative to a sphere center and axis.
 */
export function toSpherical(
  px: number, py: number, pz: number,
  cx: number, cy: number, cz: number,
  axis: THREE.Vector3
): { lat: number; lon: number; r: number } {
  const dx = px - cx, dy = py - cy, dz = pz - cz;
  const r = Math.sqrt(dx * dx + dy * dy + dz * dz);
  const v = new THREE.Vector3(dx, dy, dz);
  const cosLat = v.dot(axis) / r;
  const lat = Math.acos(Math.max(-1, Math.min(1, cosLat)));

  // Project onto plane perpendicular to axis
  const proj = v.clone().sub(axis.clone().multiplyScalar(v.dot(axis)));
  const lon = Math.atan2(proj.z, proj.x);

  return { lat, lon: lon < 0 ? lon + 2 * Math.PI : lon, r };
}

/**
 * Convert BufferGeometry to indexed form by welding vertices.
 * Returns merged positions, normals, and index array.
 */
export function weldVertices(
  positions: Float32Array,
  normals: Float32Array,
  tolerance: number = 1e-6
): { positions: Float32Array; normals: Float32Array; indices: Uint32Array } {
  const vertexCount = positions.length / 3;
  const hashMap = new Map<string, number>();
  const newPositions: number[] = [];
  const newNormals: number[] = [];
  const indices = new Uint32Array(vertexCount);
  let uniqueCount = 0;

  const factor = 1 / tolerance;

  for (let i = 0; i < vertexCount; i++) {
    const x = positions[i * 3];
    const y = positions[i * 3 + 1];
    const z = positions[i * 3 + 2];

    // Quantize position for hash key
    const key = `${Math.round(x * factor)},${Math.round(y * factor)},${Math.round(z * factor)}`;

    const existing = hashMap.get(key);
    if (existing !== undefined) {
      indices[i] = existing;
      // Average the normals
      newNormals[existing * 3] += normals[i * 3];
      newNormals[existing * 3 + 1] += normals[i * 3 + 1];
      newNormals[existing * 3 + 2] += normals[i * 3 + 2];
    } else {
      hashMap.set(key, uniqueCount);
      indices[i] = uniqueCount;
      newPositions.push(x, y, z);
      newNormals.push(normals[i * 3], normals[i * 3 + 1], normals[i * 3 + 2]);
      uniqueCount++;
    }
  }

  // Normalize the averaged normals
  for (let i = 0; i < uniqueCount; i++) {
    const nx = newNormals[i * 3];
    const ny = newNormals[i * 3 + 1];
    const nz = newNormals[i * 3 + 2];
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len > 1e-12) {
      newNormals[i * 3] /= len;
      newNormals[i * 3 + 1] /= len;
      newNormals[i * 3 + 2] /= len;
    }
  }

  return {
    positions: new Float32Array(newPositions),
    normals: new Float32Array(newNormals),
    indices,
  };
}

/**
 * Build triangle index array from sequential vertex indices (non-indexed → indexed).
 * Input: indices from weldVertices (one per original vertex).
 * Output: triangle indices (3 per triangle).
 */
export function buildTriangleIndices(weldedIndices: Uint32Array): Uint32Array {
  // weldedIndices already maps each original vertex to its welded index
  // Original vertices form triangles in groups of 3
  const triCount = weldedIndices.length / 3;
  const result = new Uint32Array(triCount * 3);
  for (let i = 0; i < triCount; i++) {
    result[i * 3] = weldedIndices[i * 3];
    result[i * 3 + 1] = weldedIndices[i * 3 + 1];
    result[i * 3 + 2] = weldedIndices[i * 3 + 2];
  }
  return result;
}
